// src/features/players/PlayerUtils.ts

import { bcLong } from '@lib2/model/util/string/BaseConverter';
import { formatSmartDate, formatTimestamp } from '@/shared/utils/date';
import type {
  PlayerViewData,
  PlayerUpdateRequest,
  PlayerGridRow,
  PlayerOverviewFormData,
  PlayerSettingsFormData,
  PlayerAdFormData,
  PlayerSecurityFormData,
  PlayerAppearanceFormData,
} from '@/lib/types/Player';

/**
 * Player data transformation utilities.
 * Handles API ↔ form data conversions and grid row computation.
 */
export class PlayerUtils {
  // --- Grid row builder ---

  static toGridRow(player: PlayerViewData): PlayerGridRow {
    const { core } = player;
    const displayId = bcLong.getStr(core.playerId);

    return {
      playerId: core.playerId,
      displayId,
      label: core.label,
      descShort: core.descShort,
      enabled: core.enabled,
      isDefault: core.isDefault,
      createdFormatted: formatSmartDate(core.created),
      createdTooltip: formatTimestamp(core.created),
      updatedFormatted: formatSmartDate(core.updated),
      updatedTooltip: formatTimestamp(core.updated),
      statusLabel: core.enabled ? 'Active' : 'Inactive',
      statusColor: core.enabled ? 'green' : 'gray',
      href: `/players/${displayId}`,
    };
  }

  static toGridRows(players: PlayerViewData[]): PlayerGridRow[] {
    return players.map(PlayerUtils.toGridRow);
  }

  // --- Overview tab transforms ---

  static transformToOverviewForm(player: PlayerViewData): PlayerOverviewFormData {
    const { core, sharing } = player;
    return {
      label: core.label,
      descShort: core.descShort ?? '',
      enabled: core.enabled,
      allowSharing: sharing.allowSharing,
      facebook: sharing.facebook,
      twitter: sharing.twitter,
      email: sharing.email,
    };
  }

  static transformOverviewFormToUpdate(
    form: PlayerOverviewFormData,
    player: PlayerViewData,
  ): PlayerUpdateRequest {
    return {
      ...PlayerUtils.buildBaseUpdateRequest(player),
      label: form.label,
      descShort: form.descShort || null,
      sharing: {
        ...player.sharing,
        allowSharing: form.allowSharing,
        facebook: form.facebook,
        twitter: form.twitter,
        email: form.email,
        // Pass through — no longer in form
        embed: player.sharing.embed,
        linkedin: player.sharing.linkedin,
        allowSpecificTime: player.sharing.allowSpecificTime,
      },
      settings: {
        ...player.settings,
      },
    };
  }

  // --- Settings tab transforms ---

  static transformToSettingsForm(player: PlayerViewData): PlayerSettingsFormData {
    const { settings, settings2 } = player;
    return {
      autoplay: settings.autoplay,
      loop: settings.loop,
      volume: settings.volume,
      playsinline: settings.playsinline,
      resumePlayback: settings.resumePlayback,
      allowSeekForward: settings.allowSeekForward,
      allowDownload: settings.allowDownload,
      fullscreen: settings.fullscreen,
      showCaptions: settings.showCaptions,
      showDvr: settings.showDvr,
      showTitle: settings.showTitle,
      // Map legacy 'video_audio' → 'video' (combo mode removed)
      videoAudioMode: settings.videoAudioMode === 'video_audio' ? 'video' : settings.videoAudioMode as string,
      responsive: settings.responsive,
      width: settings.width,
      height: settings.height,
      allowCasting: settings2.allowCasting,
      allowPIP: settings2.allowPIP,
      // ?? null: the API's circe printer drops null keys, so null arrives as undefined
      allowSkipSecs: settings2.allowSkipSecs ?? null,
      // ?? false: tolerate payloads from a backend not yet emitting the key
      allowPlaybackRate: settings2.allowPlaybackRate ?? false,
      showViewerCount: settings2.showViewerCount ?? false,
    };
  }

  static transformSettingsFormToUpdate(
    form: PlayerSettingsFormData,
    player: PlayerViewData,
  ): PlayerUpdateRequest {
    return {
      ...PlayerUtils.buildBaseUpdateRequest(player),
      settings: {
        ...player.settings,
        autoplay: form.autoplay,
        loop: form.loop,
        volume: form.volume,
        playsinline: form.playsinline,
        resumePlayback: form.resumePlayback,
        allowSeekForward: form.allowSeekForward,
        allowDownload: form.allowDownload,
        fullscreen: form.fullscreen,
        showCaptions: form.showCaptions,
        showDvr: form.showDvr,
        showTitle: form.showTitle,
        // showShortDesc, showQualities preserved via ...player.settings spread
        videoAudioMode: form.videoAudioMode as typeof player.settings.videoAudioMode,
        responsive: form.responsive,
        width: form.width,
        height: form.height,
        // endscreenMode, endscreenCustomHtml preserved via ...player.settings spread
      },
      settings2: {
        ...player.settings2,
        allowCasting: form.allowCasting,
        allowPIP: form.allowPIP,
        allowSkipSecs: form.allowSkipSecs,
        allowPlaybackRate: form.allowPlaybackRate,
        showViewerCount: form.showViewerCount,
        // gaEnabled, gaTrackingId preserved via ...player.settings2 spread
      },
    };
  }

  // --- Advertising tab transforms ---

  static transformToAdForm(player: PlayerViewData): PlayerAdFormData {
    const { ad } = player;
    return {
      adSourceType: ad.adSourceType as string,
      adSourceVasturl1: ad.adSourceVasturl1 ?? '',
      // ?? null: the API's circe printer drops null keys, so null arrives as undefined
      adSourceListId: ad.adSourceListId ?? null,
      adPreroll: ad.adPreroll,
      adMidrollInjectType: ad.adMidrollInjectType as string,
      adMidrollAutoBreakInterval: ad.adMidrollAutoBreakInterval,
      adBreakMaxDuration: ad.adBreakMaxDuration,
      adNobreakInterval: ad.adNobreakInterval,
      adBumperListId: ad.adBumperListId,
    };
  }

  static transformAdFormToUpdate(
    form: PlayerAdFormData,
    player: PlayerViewData,
  ): PlayerUpdateRequest {
    return {
      ...PlayerUtils.buildBaseUpdateRequest(player),
      ad: {
        adSourceType: form.adSourceType as typeof player.ad.adSourceType,
        adSourceVasturl1: form.adSourceVasturl1 || null,
        adSourceListId: form.adSourceListId,
        adPreroll: form.adPreroll,
        adMidrollInjectType: form.adMidrollInjectType as typeof player.ad.adMidrollInjectType,
        adMidrollAutoBreakInterval: form.adMidrollAutoBreakInterval,
        adBreakMaxDuration: form.adBreakMaxDuration,
        adNobreakInterval: form.adNobreakInterval,
        adBumperListId: form.adBumperListId,
      },
    };
  }

  // --- Security tab transforms ---

  static transformToSecurityForm(player: PlayerViewData): PlayerSecurityFormData {
    const { security } = player;
    return {
      accessControlType: security.password != null ? 'password' : 'none',
      password: security.password ?? '',
    };
  }

  static transformSecurityFormToUpdate(
    form: PlayerSecurityFormData,
    player: PlayerViewData,
    acls: typeof player.acls,
  ): PlayerUpdateRequest {
    return {
      ...PlayerUtils.buildBaseUpdateRequest(player),
      security: {
        password: form.accessControlType === 'password' ? form.password : null,
      },
      acls,
    };
  }

  // --- Appearance tab transforms ---

  static transformToAppearanceForm(player: PlayerViewData): PlayerAppearanceFormData {
    const { style, playlist } = player;
    return {
      overlayPosition: (style.overlayPosition as string) ?? 'top_left',
      overlayClickUrl: style.overlayClickUrl ?? '',
      plBarLabel: playlist.plBarLabel ?? '',
      plBarBgcolor: playlist.plBarBgcolor ?? '',
      plBarFgcolor: playlist.plBarFgcolor ?? '',
      plShowDate: playlist.plShowDate,
      plShowDescShort: playlist.plShowDescShort,
    };
  }

  static transformAppearanceFormToUpdate(
    form: PlayerAppearanceFormData,
    player: PlayerViewData,
  ): PlayerUpdateRequest {
    return {
      ...PlayerUtils.buildBaseUpdateRequest(player),
      style: {
        ...player.style,
        // colorThemeHex preserved via ...player.style spread
        overlayPosition: (form.overlayPosition as typeof player.style.overlayPosition) ?? player.style.overlayPosition,
        overlayClickUrl: form.overlayClickUrl || null,
      },
      playlist: {
        // Pass-through (removed from UI) via spread
        ...player.playlist,
        // From form (still editable)
        plBarLabel: form.plBarLabel || null,
        plBarBgcolor: form.plBarBgcolor || null,
        plBarFgcolor: form.plBarFgcolor || null,
        plShowDate: form.plShowDate,
        plShowDescShort: form.plShowDescShort,
      },
    };
  }

  // --- Base update request builder ---

  /**
   * Build the full IPlayerUpdateReq from existing player data.
   * Tab-specific transforms override specific sections.
   */
  private static buildBaseUpdateRequest(player: PlayerViewData): PlayerUpdateRequest {
    return {
      acls: player.acls,
      ad: player.ad,
      descShort: player.core.descShort,
      isDefault: player.core.isDefault,
      label: player.core.label,
      playlist: player.playlist,
      security: player.security,
      settings: player.settings,
      settings2: player.settings2,
      sharing: player.sharing,
      style: player.style,
    };
  }
}
